<template>
  <div class="qclaw-jobs-page full-container">
    <div class="qclaw-jobs-layout">
      <div class="qclaw-jobs-panel">
        <div class="qclaw-jobs-toolbar">
          <div class="qclaw-jobs-toolbar-text">
            <span class="qclaw-jobs-toolbar-title">Agent任务</span>
            <span class="qclaw-jobs-toolbar-desc">管理 Agent 任务与查看提交成果</span>
          </div>
          <div class="qclaw-jobs-toolbar-actions">
            <Input
              v-model="groupFilter"
              placeholder="分组"
              clearable
              style="width: 120px; margin-right: 8px;"
              @on-enter="fetchList"
              @on-blur="fetchList"
              @on-clear="fetchList"
            />
            <RadioGroup
              v-model="jobViewFilter"
              type="button"
              style="margin-right: 12px;"
              @on-change="onJobViewFilterChange"
            >
              <Radio label="plaza">任务广场</Radio>
              <Radio label="mine">我发布的</Radio>
              <Radio label="completed">已完成的</Radio>
            </RadioGroup>
            <Input
              v-model="searchText"
              placeholder="搜索名称或描述"
              clearable
              style="width: 220px; margin-right: 8px;"
            />
            <Button type="success" icon="md-add" @click="openCreate">新建任务</Button>
            <Button icon="md-refresh" @click="fetchList">刷新</Button>
          </div>
        </div>
        <div class="qclaw-jobs-table-wrap">
          <BaseTable
            ref="table"
            full
            class="qclaw-jobs-main-table"
            body-scroll-x="hidden"
            :option="{ stripe: true }"
            :columns="columns"
            :data="filteredItems"
            :loading="loading"
            border
            @event="handleTableEvent"
          />
        </div>
      </div>
    </div>

    <Modal
      v-model="jobModalVisible"
      :title="jobModalTitle"
      width="560"
      :loading="jobSubmitting"
      @on-ok="handleJobSubmit"
      @on-cancel="jobModalVisible = false"
    >
      <Form ref="jobFormRef" :model="jobForm" :rules="jobRules" :label-width="88">
        <FormItem label="名称" prop="name">
          <Input v-model="jobForm.name" placeholder="任务名称" />
        </FormItem>
        <FormItem label="分组" prop="group">
          <Input v-model="jobForm.group" placeholder="分组，如：培训" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input v-model="jobForm.description" type="textarea" :rows="3" placeholder="任务描述" />
        </FormItem>
        <FormItem label="校验规则" prop="validation">
          <Input v-model="jobForm.validation" type="textarea" :rows="4" placeholder="校验规则或提示词" />
        </FormItem>
        <FormItem label="启用">
          <i-switch v-model="jobForm.active">
            <span slot="open">启用</span>
            <span slot="close">停用</span>
          </i-switch>
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="validationModalVisible"
      :title="validationModalTitle"
      width="720"
      :footer-hide="true"
    >
      <Input
        v-model="validationModalText"
        type="textarea"
        :rows="16"
        readonly
        placeholder="暂无内容"
      />
    </Modal>

    <Modal
      v-model="suggestionModalVisible"
      :title="suggestionModalTitle"
      width="640"
      :footer-hide="true"
    >
      <Input
        v-model="suggestionModalText"
        type="textarea"
        :rows="14"
        readonly
        placeholder="暂无评语"
      />
    </Modal>

    <Modal
      v-model="manualScoreVisible"
      title="手动评分"
      width="480"
      :loading="scoreSavingId != null"
      @on-ok="submitManualScore"
      @on-cancel="manualScoreVisible = false"
    >
      <Form :label-width="72">
        <FormItem label="得分">
          <InputNumber
            v-model="manualScoreForm.score"
            :min="0"
            :max="100"
            :step="1"
            style="width: 120px;"
          />
        </FormItem>
        <FormItem label="评语">
          <Input
            v-model="manualScoreForm.suggestion"
            type="textarea"
            :rows="5"
            placeholder="评价意见"
          />
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="aiScoreVisible"
      :title="''"
      width="960"
      :footer-hide="true"
      :closable="!aiScoreLoading"
      :mask-closable="!aiScoreLoading"
      class-name="qclaw-ai-score-modal no-modal-title"
      @on-cancel="closeAiScore"
    >
      <a slot="header" class="modal-header-close" @click="!aiScoreLoading && closeAiScore()">
        <Icon type="md-close" size="20" />
      </a>
      <h3 class="inner-modal-title">AI 评分</h3>
      <div class="ai-score-panel">
        <div v-if="aiScorePhase === 'error'" class="ai-score-error">
          <Icon type="md-alert" size="40" color="#ed4014" />
          <p>{{ aiScoreError || '评分失败' }}</p>
          <Button type="primary" @click="retryAiScore">重试</Button>
        </div>
        <div v-else class="ai-score-layout">
          <div class="ai-score-left">
            <div class="ai-score-section-title">提交内容</div>
            <div class="ai-score-md-body">
              <BaseMarkdownView v-if="aiScoreRow" :value="aiScoreRow.commit || ''" />
            </div>
          </div>
          <div class="ai-score-right">
            <div v-if="aiScorePhase === 'loading'" class="ai-score-loading">
              <div class="ai-score-loading-orbit">
                <Icon type="ios-bulb" size="32" />
              </div>
              <p class="ai-score-loading-title">AI 正在评分</p>
              <p class="ai-score-loading-desc">
                <template v-if="aiScoreRetryCount > 0">
                  格式校验未通过，正在第 {{ aiScoreRetryCount + 1 }} / {{ aiScoreMaxRetry }} 次重新生成…
                </template>
                <template v-else>正在根据任务标准分析提交内容…</template>
              </p>
              <div v-if="aiScoreStreamText" class="ai-score-stream-preview">
                <pre>{{ aiScoreStreamText }}</pre>
              </div>
            </div>
            <template v-else-if="aiScorePhase === 'result'">
              <div class="ai-score-section-title">评分结果</div>
              <div class="ai-score-form">
                <div class="ai-score-form-row">
                  <span class="ai-score-form-label">分值</span>
                  <div class="ai-score-form-score">
                    <InputNumber
                      v-model="aiScoreForm.score"
                      :min="0"
                      :max="100"
                      :step="1"
                      style="width: 100px;"
                    />
                    <span class="ai-score-form-unit">分</span>
                    <Tag :color="scoreTierColor(aiScoreForm.score)">{{ scoreTierLabel(aiScoreForm.score) }}</Tag>
                  </div>
                </div>
                <div class="ai-score-form-row ai-score-form-row--block">
                  <span class="ai-score-form-label">评价意见</span>
                  <Input
                    v-model="aiScoreForm.suggestion"
                    type="textarea"
                    :rows="10"
                    placeholder="AI 生成的评价意见，可修改"
                    class="ai-score-suggestion-input"
                  />
                </div>
              </div>
              <div class="ai-score-actions">
                <Button @click="closeAiScore">取消</Button>
                <Button
                  type="primary"
                  :loading="scoreSavingId != null"
                  @click="submitAiScore"
                >提交</Button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      v-model="commitDetailVisible"
      :title="''"
      width="800"
      :footer-hide="true"
      class-name="qclaw-commit-detail-modal no-modal-title"
    >
      <a slot="header" class="modal-header-close" @click="commitDetailVisible = false">
        <Icon type="md-close" size="20" />
      </a>
      <h3 v-if="commitDetailRow" class="inner-modal-title">提交详情</h3>
      <div v-if="commitDetailRow" class="commit-card commit-card--modal">
        <div class="commit-card-head">
          <div class="commit-card-user">
            <BaseNameBoard :uid="commitDetailRow.created_by" />
            <span class="commit-card-time">{{ formatRelativeTime(commitDetailRow.created_at) }}</span>
          </div>
          <div class="commit-card-score-wrap">
            <span class="commit-card-score-label">得分</span>
            <InputNumber
              v-if="canScoreCommit()"
              :value="commitDetailScore"
              :min="0"
              :max="100"
              :step="1"
              size="small"
              :disabled="scoreSavingId === commitDetailRow.id"
              style="width: 88px;"
              @on-change="(v) => patchCommitScore(commitDetailRow, v)"
            />
            <span v-else class="commit-card-score-val">{{
              isCommitScored(commitDetailRow) ? displayScore(commitDetailRow.score) : '未评'
            }}</span>
          </div>
        </div>
        <div class="commit-card-body commit-detail-body">
          <BaseMarkdownView :value="commitDetailText" />
        </div>
        <div v-if="commitDetailFiles.length" class="commit-card-files">
          <span class="commit-card-files-label">附件</span>
          <div class="commit-file-list">
            <template v-for="(url, idx) in commitDetailFiles">
              <a
                v-if="!isImageUrl(url)"
                :key="'f-' + idx"
                class="commit-file-link"
                :href="normalizeFileUrl(url)"
                target="_blank"
                rel="noopener noreferrer"
              >{{ fileLabel(url, idx) }}</a>
              <a
                v-else
                :key="'i-' + idx"
                class="commit-file-image"
                :href="normalizeFileUrl(url)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img :src="normalizeFileUrl(url)" :alt="fileLabel(url, idx)">
              </a>
            </template>
          </div>
        </div>
        <div class="commit-card-footer">
          <div class="commit-card-footer-label">评价意见</div>
          <div class="commit-card-suggestion">{{ commitDetailRow.suggestion || '暂无评价' }}</div>
          <div v-if="commitDetailRow.suggested_at" class="commit-card-suggested-at">
            评价于 {{ formatTime(commitDetailRow.suggested_at) }}
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      v-model="jobDetailVisible"
      :title="''"
      width="1000"
      :footer-hide="true"
      class-name="qclaw-job-detail-modal no-modal-title"
      @on-visible-change="onJobDetailVisible"
    >
      <template v-if="commitsJobRow">
        <h2 class="inner-modal-title">{{ commitsJobRow.name || '任务详情' }}</h2>
        <div class="job-detail-card">
          <div class="job-detail-card-head">
            <div class="job-detail-tags">
              <Tag v-if="commitsJobRow.group" color="primary">{{ commitsJobRow.group }}</Tag>
              <Tag :color="jobActive(commitsJobRow) ? 'success' : 'default'">
                {{ jobActive(commitsJobRow) ? '启用' : '停用' }}
              </Tag>
              <a
                v-if="commitsJobRow.validation"
                class="job-link job-detail-validation"
                @click="openValidationView(commitsJobRow)"
              >查看校验规则</a>
            </div>
            <div class="job-detail-meta">
              <BaseNameBoard :uid="commitsJobRow.created_by" />
              <span class="job-detail-meta-time">{{ formatTime(commitsJobRow.created_at) }}</span>
            </div>
          </div>
          <div v-if="commitsJobRow.description" class="job-detail-desc">
            {{ commitsJobRow.description }}
          </div>
        </div>

        <div class="commits-section">
          <div class="commits-section-head">
            <span class="commits-section-title">提交成果</span>
          </div>
          <div class="commits-stats">
            <span
              class="commits-stat"
              :class="{ active: commitFilter === 'all' }"
              @click="commitFilter = 'all'"
            >提交 {{ commitStats.total }} 份</span>
            <span
              class="commits-stat"
              :class="{ active: commitFilter === 'scored' }"
              @click="commitFilter = 'scored'"
            >已评分 {{ commitStats.scored }} 份</span>
            <span
              class="commits-stat commits-stat--excellent"
              :class="{ active: commitFilter === 'excellent' }"
              @click="commitFilter = 'excellent'"
            >优秀 {{ commitStats.excellent }} 份</span>
            <span
              class="commits-stat commits-stat--qualified"
              :class="{ active: commitFilter === 'qualified' }"
              @click="commitFilter = 'qualified'"
            >合格 {{ commitStats.qualified }} 份</span>
            <span
              class="commits-stat commits-stat--unqualified"
              :class="{ active: commitFilter === 'unqualified' }"
              @click="commitFilter = 'unqualified'"
            >不合格 {{ commitStats.unqualified }} 份</span>
          </div>
          <div class="commits-table-wrap">
            <BaseTable
              ref="commitsTable"
              :columns="commitColumns"
              :data="filteredCommitItems"
              border
              :paged="false"
              :loading="commitsLoading"
              @on-row-click="onCommitRowClick"
            />
          </div>
        </div>
      </template>
      <a slot="header" class="modal-header-close" @click="jobDetailVisible = false">
        <Icon type="md-close" size="20" />
      </a>
    </Modal>
  </div>
</template>

<script>
import BaseMarkdownView from '@/components/base/MarkdownView';
import { formatRelativeTime as formatRelativeTimeUtil } from '@/utils/format';

const API_BASE = '/ai/qclaw-jobs';

const JOB_STATUS_TYPES = [
  { value: '准备中', name: '准备中', color: '#2d8cf0' },
  { value: '已发布', name: '已发布', color: '#19be6b' },
  { value: '已结束', name: '已结束', color: '#999' },
];

function extractList(res) {
  const body = res && res.data;
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.list)) return body.list;
  if (body.data && Array.isArray(body.data.items)) return body.data.items;
  return [];
}

function jobActive(row) {
  if (row == null) return false;
  return row.active === true || row.active === 1 || row.active === '1';
}

function sameId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

function parseFileUrls(files) {
  if (files == null || files === '') return [];
  if (Array.isArray(files)) return files.map((u) => String(u).trim()).filter(Boolean);
  return String(files).split(',').map((s) => s.trim()).filter(Boolean);
}

function fileLabel(url, index) {
  const raw = String(url || '');
  const name = raw.split('/').pop() || raw.split('\\').pop();
  if (name && name.length < 80) return decodeURIComponent(name);
  return `附件${index + 1}`;
}

function isImageUrl(url) {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(String(url || ''));
}

function getScoreTier(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  if (n >= 90) return 'excellent';
  if (n >= 60) return 'qualified';
  return 'unqualified';
}

const AI_SCORE_MAX_RETRY = 3;

function cleanAiJsonOutput(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  s = s.replace(/^\uFEFF/, '');
  s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  const firstBrace = s.indexOf('{');
  const lastBrace = s.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    s = s.slice(firstBrace, lastBrace + 1);
  }
  return s.trim();
}

function pickDefined() {
  for (let i = 0; i < arguments.length; i += 1) {
    const v = arguments[i];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

function normalizeAiScoreObj(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const score = Number(pickDefined(obj.score, obj.Score, obj.得分));
  if (!Number.isFinite(score)) return null;
  return {
    score: Math.min(100, Math.max(0, Math.round(score))),
    suggestion: String(pickDefined(obj.suggestion, obj.comment, obj.评语, obj.feedback) || '').trim(),
  };
}

function parseAiScoreFromPlainText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;

  let score = null;
  const scorePatterns = [
    /(?:score|分数|得分|分值)\s*[:：]\s*(\d+(?:\.\d+)?)/i,
    /["']?(?:score|分数|得分)["']?\s*[:：]\s*(\d+(?:\.\d+)?)/i,
    /\b(\d{1,3})\s*分\b/,
  ];
  for (let i = 0; i < scorePatterns.length; i += 1) {
    const m = raw.match(scorePatterns[i]);
    if (m) {
      score = Number(m[1]);
      break;
    }
  }
  if (!Number.isFinite(score)) return null;

  let suggestion = '';
  const sugPatterns = [
    /(?:suggestion|评语|评价意见|意见|comment)\s*[:：]\s*["']?([\s\S]+?)["']?\s*$/i,
    /(?:suggestion|评语|评价意见|意见|comment)\s*[:：]\s*([\s\S]+)/i,
  ];
  for (let i = 0; i < sugPatterns.length; i += 1) {
    const m = raw.match(sugPatterns[i]);
    if (m && m[1].trim()) {
      suggestion = m[1].trim().replace(/^["']|["']$/g, '');
      break;
    }
  }
  if (!suggestion) {
    suggestion = raw
      .replace(/```[\s\S]*?```/g, '')
      .replace(/(?:score|分数|得分|分值)\s*[:：]\s*\d+[^\n]*/gi, '')
      .replace(/^\{[\s\S]*\}$/m, '')
      .trim();
  }

  return {
    score: Math.min(100, Math.max(0, Math.round(score))),
    suggestion: suggestion || '（AI 未提供评语）',
  };
}

/** 解析 AI 评分结果：优先 JSON，失败则尝试从文本提取 */
function tryParseAiScoreResult(text) {
  const raw = String(text || '').trim();
  if (!raw) {
    return { ok: false, error: 'AI 返回内容为空' };
  }

  const candidates = [cleanAiJsonOutput(raw), raw];
  for (let i = 0; i < candidates.length; i += 1) {
    const piece = candidates[i];
    if (!piece) continue;
    try {
      const obj = JSON.parse(piece);
      const data = normalizeAiScoreObj(obj);
      if (data) return { ok: true, data };
    } catch (e) { /* try next */ }
  }

  const fromText = parseAiScoreFromPlainText(raw);
  if (fromText) return { ok: true, data: fromText };

  return { ok: false, error: '无法解析 AI 返回的评分 JSON，请重试' };
}

export default {
  components: { BaseMarkdownView },
  data() {
    return {
      loading: false,
      items: [],
      groupFilter: '培训',
      jobViewFilter: 'plaza',
      searchText: '',
      jobModalVisible: false,
      jobModalTitle: '新建任务',
      jobSubmitting: false,
      jobEditId: null,
      jobForm: {
        name: '',
        group: '培训',
        description: '',
        validation: '',
        active: true,
      },
      jobRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        group: [{ required: true, message: '请输入分组', trigger: 'blur' }],
      },
      validationModalVisible: false,
      validationModalTitle: '校验规则',
      validationModalText: '',
      suggestionModalVisible: false,
      suggestionModalTitle: '评语',
      suggestionModalText: '',
      commitDetailVisible: false,
      commitDetailTitle: '提交内容',
      commitDetailText: '',
      commitDetailRow: null,
      commitDetailFiles: [],
      jobDetailVisible: false,
      commitsJobRow: null,
      commitsLoading: false,
      commitItems: [],
      commitFilter: 'all',
      scoreSavingId: null,
      manualScoreVisible: false,
      manualScoreRow: null,
      manualScoreForm: { score: null, suggestion: '' },
      aiScoreVisible: false,
      aiScoreRow: null,
      aiScorePhase: 'loading',
      aiScoreLoading: false,
      aiScoreStreamText: '',
      aiScoreResult: null,
      aiScoreForm: { score: null, suggestion: '' },
      aiScoreError: '',
      aiScoreRetryCount: 0,
      aiScoreMaxRetry: AI_SCORE_MAX_RETRY,
      _aiScoreReader: null,
    };
  },
  computed: {
    columns() {
      return [
        { type: 'index', width: 56, title: '#' },
        {
          title: '名称',
          key: 'name',
          minWidth: 120,
          render: (h, { row }) => h(
            'a',
            {
              class: 'job-name-link',
              on: { click: () => this.openJobDetail(row) },
            },
            row.name || '—',
          ),
        },
        {
          title: '分组',
          key: 'group',
          width: 100,
        },
        {
          title: '状态',
          key: 'status',
          width: 100,
          align: 'center',
          type: 'type',
          option: {
            types: JOB_STATUS_TYPES,
            editable: true,
          },
        },
        {
          title: '描述',
          key: 'description',
          minWidth: 160,
          ellipsis: true,
          render: (h, { row }) => h('span', row.description || '—'),
        },
        {
          title: '校验规则',
          key: 'validation',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            if (!row.validation) return h('span', '—');
            return h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.openValidationView(row) },
            }, '查看');
          },
        },
        {
          title: '启用',
          key: 'active',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            if (!this.canEditOrDeleteJob(row)) {
              return h('span', jobActive(row) ? '启用' : '停用');
            }
            return h('i-switch', {
              props: {
                value: jobActive(row),
              },
              on: {
                'on-change': (v) => this.patchActive(row, v),
              },
            }, [h('span', { slot: 'open' }, '启用'), h('span', { slot: 'close' }, '停用')]);
          },
        },
        {
          title: '提交数量',
          key: 'commits_count',
          width: 100,
          align: 'center',
          render: (h, { row }) => {
            const n = row.commits_count;
            const count = n == null || n === '' ? 0 : n;
            return h(
              'a',
              {
                class: 'commits-count-link',
                style: count ? '' : 'color:#aaa',
                on: { click: () => this.openJobDetail(row) },
              },
              String(count),
            );
          },
        },
        {
          title: '创建人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 156,
          render: (h, { row }) => h('span', this.formatTime(row.created_at)),
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          align: 'center',
          render: (h, { row }) => {
            if (!this.canEditOrDeleteJob(row)) {
              return h('span', { style: { color: 'var(--subtext2)' } }, '—');
            }
            return h('div', [
              h('Button', {
                props: { size: 'small', type: 'text' },
                style: { marginRight: '6px' },
                on: { click: () => this.openEdit(row) },
              }, '编辑'),
              h('Button', {
                props: { size: 'small', type: 'text' },
                on: { click: () => this.confirmDeleteJob(row) },
              }, '删除'),
            ]);
          },
        },
      ];
    },
    session() {
      return this.$store.getters['session/session'] || {};
    },
    filteredItems() {
      let list = [...this.items];
      if (this.jobViewFilter === 'mine') {
        list = list.filter((item) => sameId(item.created_by, this.session.id));
      } else if (this.jobViewFilter === 'completed') {
        list = list.filter((item) => item.status === '已完成');
      } else {
        list = list.filter(
          (item) =>
            jobActive(item) &&
            item.status !== '已完成' &&
            item.status !== '已关闭',
        );
      }
      const q = (this.searchText || '').trim().toLowerCase();
      if (q) {
        list = list.filter(
          (item) =>
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.description && String(item.description).toLowerCase().includes(q)),
        );
      }
      return list;
    },
    commitsJobId() {
      return this.commitsJobRow ? this.commitsJobRow.id : null;
    },
    commitDetailScore() {
      return this.commitScoreValue(this.commitDetailRow || {});
    },
    commitStats() {
      const stats = {
        total: this.commitItems.length,
        scored: 0,
        excellent: 0,
        qualified: 0,
        unqualified: 0,
      };
      this.commitItems.forEach((item) => {
        if (!this.isCommitScored(item)) return;
        stats.scored += 1;
        const n = this.commitScoreValue(item);
        if (n == null) return;
        const tier = getScoreTier(n);
        if (tier === 'excellent') stats.excellent += 1;
        else if (tier === 'qualified') stats.qualified += 1;
        else stats.unqualified += 1;
      });
      return stats;
    },
    filteredCommitItems() {
      const f = this.commitFilter;
      const myId = this.session && this.session.id;
      let list = this.commitItems.filter((item) => {
        const scored = this.isCommitScored(item);
        const n = scored ? this.commitScoreValue(item) : null;
        const tier = n != null ? getScoreTier(n) : null;
        if (f === 'all') return true;
        if (f === 'scored') return scored;
        if (f === 'unscored') return !scored;
        if (f === 'excellent') return scored && tier === 'excellent';
        if (f === 'qualified') return scored && tier === 'qualified';
        if (f === 'unqualified') return scored && tier === 'unqualified';
        return true;
      });
      list.sort((a, b) => {
        const aMine = sameId(a.created_by, myId);
        const bMine = sameId(b.created_by, myId);
        if (aMine && !bMine) return -1;
        if (!aMine && bMine) return 1;
        const ta = new Date(a.created_at).getTime();
        const tb = new Date(b.created_at).getTime();
        if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
        return tb - ta;
      });
      return list;
    },
    commitColumns() {
      return [
        { type: 'index', width: 52, title: '#' },
        {
          title: '提交人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '提交时间',
          key: 'created_at',
          width: 140,
          render: (h, { row }) => h('span', this.formatRelativeTime(row.created_at)),
        },
        {
          title: '得分',
          key: 'score',
          width: 72,
          align: 'center',
          render: (h, { row }) => {
            if (!this.isCommitScored(row)) return h('span', '—');
            return h('span', { class: 'commit-table-score' }, this.displayScore(row.score));
          },
        },
        {
          title: '等级',
          key: 'tier',
          width: 80,
          align: 'center',
          render: (h, { row }) => {
            if (!this.isCommitScored(row)) return h('Tag', { props: { color: 'default' } }, '未评');
            const n = this.commitScoreValue(row);
            if (n == null) return h('Tag', { props: { color: 'default' } }, '未评');
            const tier = getScoreTier(n);
            const map = {
              excellent: { color: 'success', text: '优秀' },
              qualified: { color: 'primary', text: '合格' },
              unqualified: { color: 'error', text: '不合格' },
            };
            const t = map[tier] || map.unqualified;
            return h('Tag', { props: { color: t.color } }, t.text);
          },
        },
        {
          title: '评语',
          key: 'suggestion',
          width: 80,
          align: 'center',
          render: (h, { row }) => {
            if (!row.suggestion) return h('span', '—');
            return h('Button', {
              props: { size: 'small', type: 'text' },
              on: {
                click: (e) => {
                  e.stopPropagation();
                  this.openSuggestionView(row);
                },
              },
            }, '查看');
          },
        },
        {
          title: '提交内容',
          key: 'commit',
          minWidth: 160,
          render: (h, { row }) => {
            const text = row.commit || '';
            const preview = text.length > 36 ? `${text.slice(0, 36)}…` : (text || '—');
            return h('a', {
              class: 'commit-link',
              on: {
                click: (e) => {
                  e.stopPropagation();
                  this.openCommitDetail(row);
                },
              },
            }, preview);
          },
        },
        {
          title: '附件',
          key: 'files',
          type: 'files',
          width: 120,
          align: 'center',
        },
        {
          title: '操作',
          key: 'action',
          width: 108,
          align: 'center',
          render: (h, { row }) => {
            const btns = [];
            if (this.canScoreCommit()) {
              btns.push(
                h('Tooltip', { props: { content: '手动评分', transfer: true } }, [
                  h('Button', {
                    props: { type: 'text', icon: 'md-create', size: 'small' },
                    on: {
                      click: (e) => {
                        e.stopPropagation();
                        this.openManualScore(row);
                      },
                    },
                  }),
                ]),
                h('Tooltip', { props: { content: 'AI 评分', transfer: true } }, [
                  h('Button', {
                    props: { type: 'text', icon: 'md-bulb', size: 'small' },
                    on: {
                      click: (e) => {
                        e.stopPropagation();
                        this.openAiScore(row);
                      },
                    },
                  }),
                ]),
              );
            }
            if (this.canDeleteCommit(row)) {
              btns.push(
                h('Tooltip', { props: { content: '删除', transfer: true } }, [
                  h('Button', {
                    props: { type: 'text', icon: 'md-trash', size: 'small' },
                    class: 'commit-delete-icon',
                    on: {
                      click: (e) => {
                        e.stopPropagation();
                        this.confirmDeleteCommit(row);
                      },
                    },
                  }),
                ]),
              );
            }
            if (!btns.length) return h('span', '—');
            return h('div', { class: 'commit-action-icons' }, btns);
          },
        },
      ];
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchList();
    });
  },
  beforeDestroy() {
    this.abortAiScoreRequest();
  },
  methods: {
    jobActive,
    isImageUrl,
    fileLabel,
    parseFileUrls,
    formatRelativeTime(v) {
      if (v == null || v === '') return '—';
      const d = typeof v === 'number' && v < 1e12 ? new Date(v * 1000) : new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return formatRelativeTimeUtil(d);
    },
    displayScore(score) {
      if (score === null || score === undefined || score === '') return '—';
      return String(score);
    },
    scoreTierLabel(score) {
      const tier = getScoreTier(score);
      if (tier === 'excellent') return '优秀';
      if (tier === 'qualified') return '合格';
      if (tier === 'unqualified') return '不合格';
      return '未评';
    },
    scoreTierColor(score) {
      const tier = getScoreTier(score);
      if (tier === 'excellent') return 'success';
      if (tier === 'qualified') return 'primary';
      if (tier === 'unqualified') return 'error';
      return 'default';
    },
    isCommitScored(row) {
      if (!row) return false;
      const v = row.suggested_at;
      return v != null && v !== '';
    },
    commitScoreValue(row) {
      if (!row || !this.isCommitScored(row)) return null;
      const v = row.score;
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    normalizeFileUrl(url) {
      const u = String(url || '').trim();
      if (!u) return '';
      if (/^https?:\/\//i.test(u)) return u;
      return u.startsWith('/') ? u : `/${u}`;
    },
    isAdmin() {
      return this.session.is_admin === true;
    },
    canEditOrDeleteJob(row) {
      if (!this.session || !this.session.id) return false;
      return sameId(this.session.id, row.created_by) || this.isAdmin();
    },
    canScoreCommit() {
      if (!this.commitsJobRow || !this.session || !this.session.id) return false;
      return sameId(this.session.id, this.commitsJobRow.created_by);
    },
    canDeleteCommit(commitRow) {
      if (!this.session || !this.session.id) return false;
      if (this.isAdmin()) return true;
      if (this.commitsJobRow && sameId(this.session.id, this.commitsJobRow.created_by)) return true;
      return sameId(this.session.id, commitRow.created_by);
    },
    formatTime(v) {
      if (v == null || v === '') return '—';
      const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    onJobViewFilterChange() {
      this.fetchList();
    },
    handleTableEvent(e) {
      if (!e || e.type !== 'change-type') return;
      const row = this.items.find((item) => item.id === e.data.id) || e.data;
      if (!this.canEditOrDeleteJob(row)) {
        this.$Message.warning('仅管理员或创建人可修改状态');
        this.fetchList();
        return;
      }
      this.patchJobStatus(e.data);
    },
    async patchJobStatus(data) {
      const id = data && data.id;
      const status = data && data.status;
      if (id == null || status == null || status === '') return;
      try {
        await this.$api.patch(`${API_BASE}/${id}`, { status });
        const item = this.items.find((i) => i.id === id);
        if (item) item.status = status;
        this.$Message.success('状态已更新');
      } catch (e) {
        this.$Message.error(e.message || '状态更新失败');
        await this.fetchList();
      }
    },
    async fetchList() {
      this.loading = true;
      try {
        const params = {};
        if ((this.groupFilter || '').trim()) {
          params.group = this.groupFilter.trim();
        }
        if (this.jobViewFilter === 'mine' && this.session.id) {
          params.created_by = this.session.id;
        } else if (this.jobViewFilter === 'completed') {
          params.status = '已完成';
        } else if (this.jobViewFilter === 'plaza') {
          params.scope = 'plaza';
        }
        const res = await this.$api.get(API_BASE, { params });
        this.items = extractList(res);
      } catch (e) {
        this.$Message.error(e.message || '加载失败');
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    resetJobForm() {
      this.jobForm = {
        name: '',
        group: this.groupFilter || '培训',
        description: '',
        validation: '',
        active: true,
      };
    },
    openCreate() {
      this.jobModalTitle = '新建任务';
      this.jobEditId = null;
      this.resetJobForm();
      this.jobModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.jobFormRef) this.$refs.jobFormRef.resetFields();
      });
    },
    openEdit(row) {
      if (!this.canEditOrDeleteJob(row)) {
        this.$Message.warning('仅管理员或创建人可编辑');
        return;
      }
      this.jobModalTitle = '编辑任务';
      this.jobEditId = row.id;
      this.jobForm = {
        name: row.name || '',
        group: row.group || '',
        description: row.description || '',
        validation: row.validation || '',
        active: jobActive(row),
      };
      this.jobModalVisible = true;
    },
    async handleJobSubmit() {
      try {
        await this.$refs.jobFormRef.validate();
        this.jobSubmitting = true;
        const payload = {
          name: (this.jobForm.name || '').trim(),
          group: (this.jobForm.group || '').trim(),
          description: this.jobForm.description || '',
          validation: this.jobForm.validation || '',
          active: !!this.jobForm.active,
        };
        if (this.jobEditId) {
          await this.$api.patch(`${API_BASE}/${this.jobEditId}`, payload);
          this.$Message.success('已保存');
        } else {
          await this.$api.post(API_BASE, payload);
          this.$Message.success('创建成功');
        }
        this.jobModalVisible = false;
        await this.fetchList();
        return true;
      } catch (e) {
        if (e && e.message) this.$Message.error(e.message);
        return false;
      } finally {
        this.jobSubmitting = false;
      }
    },
    async patchActive(row, active) {
      if (!this.canEditOrDeleteJob(row)) return;
      try {
        await this.$api.patch(`${API_BASE}/${row.id}`, { active: !!active });
        this.$Message.success('已更新');
        await this.fetchList();
      } catch (e) {
        this.$Message.error(e.message || '更新失败');
        await this.fetchList();
      }
    },
    openValidationView(row) {
      this.validationModalTitle = `校验规则 — ${row.name || ''}`;
      this.validationModalText = row.validation || '';
      this.validationModalVisible = true;
    },
    openSuggestionView(row) {
      this.suggestionModalTitle = '评价意见';
      this.suggestionModalText = row.suggestion || '';
      this.suggestionModalVisible = true;
    },
    openCommitDetail(row) {
      this.commitDetailRow = row;
      this.commitDetailTitle = '提交内容';
      this.commitDetailText = row.commit || '';
      this.commitDetailFiles = parseFileUrls(row.files);
      this.commitDetailVisible = true;
    },
    openJobDetail(row) {
      this.commitsJobRow = row;
      this.commitFilter = 'all';
      this.commitItems = [];
      this.jobDetailVisible = true;
      this.fetchCommits();
    },
    onJobDetailVisible(visible) {
      if (!visible) {
        this.commitsJobRow = null;
        this.commitItems = [];
        this.commitFilter = 'all';
      }
    },
    onCommitRowClick(row) {
      if (row && row.id != null) {
        this.openCommitDetail(row);
      }
    },
    openManualScore(row) {
      if (!this.canScoreCommit()) return;
      this.manualScoreRow = row;
      this.manualScoreForm = {
        score: this.commitScoreValue(row),
        suggestion: row.suggestion || '',
      };
      this.manualScoreVisible = true;
    },
    async submitManualScore() {
      if (!this.manualScoreRow || !this.canScoreCommit()) return false;
      const score = this.manualScoreForm.score;
      if (score === null || score === undefined || score === '') {
        this.$Message.warning('请输入得分');
        return false;
      }
      try {
        await this.patchCommitScore(
          this.manualScoreRow,
          score,
          (this.manualScoreForm.suggestion || '').trim(),
        );
        this.manualScoreVisible = false;
        return true;
      } catch (e) {
        return false;
      }
    },
    buildAiScorePrompt(row) {
      const job = this.commitsJobRow || {};
      return `你是作业评分助手。请根据任务描述与评价标准，对学员提交进行客观评分。

【任务描述】
${job.description || '（无）'}

【评价标准】
${job.validation || '（无）'}

【学员提交内容】
${row.commit || '（无）'}

评分规则：
- 90分及以上为优秀
- 60-89分为合格
- 60分以下为不合格

请严格只返回 JSON 对象，不要 markdown 代码块或其它说明文字，格式如下：
{"score": 整数0-100, "suggestion": "评语文字"}`;
    },
    applyAiScoreResult(data) {
      this.aiScoreResult = data;
      this.aiScoreForm = {
        score: data.score,
        suggestion: data.suggestion,
      };
      this.aiScorePhase = 'result';
    },
    async fetchAiScoreStreamText() {
      if (typeof this.$ai.chat !== 'function') {
        throw new Error('AI.chat 未就绪');
      }
      const reader = await this.$ai.chat({
        model_id: 'deepseek-v4',
        messages: [
          {
            role: 'user',
            content: this.buildAiScorePrompt(this.aiScoreRow),
          },
        ],
        stream: true,
        value_type: 'text',
      });
      if (!reader || typeof reader.read !== 'function') {
        throw new Error('未能建立流式连接');
      }
      this._aiScoreReader = reader;
      const decoder = new TextDecoder();
      let text = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            const chunk = value instanceof Uint8Array
              ? decoder.decode(value, { stream: true })
              : String(value);
            text += chunk;
            this.aiScoreStreamText = text.slice(-800);
          }
        }
      } finally {
        this._aiScoreReader = null;
      }
      return text;
    },
    abortAiScoreRequest() {
      if (this._aiScoreReader) {
        try {
          this._aiScoreReader.cancel();
        } catch (e) { /* ignore */ }
        this._aiScoreReader = null;
      }
    },
    async runAiScore(resetRetry = true) {
      if (!this.aiScoreRow || !this.canScoreCommit()) return;
      if (resetRetry) this.aiScoreRetryCount = 0;

      this.aiScorePhase = 'loading';
      this.aiScoreLoading = true;
      this.aiScoreStreamText = '';
      this.aiScoreResult = null;
      this.aiScoreForm = { score: null, suggestion: '' };
      this.aiScoreError = '';
      this.abortAiScoreRequest();

      let lastError = 'AI 评分失败';

      try {
        for (let attempt = 0; attempt < AI_SCORE_MAX_RETRY; attempt += 1) {
          if (!this.aiScoreVisible) return;

          this.aiScoreRetryCount = attempt;
          if (attempt > 0) {
            this.aiScoreStreamText = `解析失败，正在重新生成（${attempt + 1}/${AI_SCORE_MAX_RETRY}）…`;
            this.abortAiScoreRequest();
          } else {
            this.aiScoreStreamText = '';
          }

          try {
            const text = await this.fetchAiScoreStreamText();
            const parsed = tryParseAiScoreResult(text);
            if (parsed.ok) {
              this.applyAiScoreResult(parsed.data);
              return;
            }
            lastError = parsed.error || lastError;
          } catch (e) {
            if (e && e.name === 'AbortError') return;
            lastError = (e && e.message) ? e.message : lastError;
            break;
          }
        }

        this.aiScorePhase = 'error';
        this.aiScoreError = `${lastError}（已自动重试 ${AI_SCORE_MAX_RETRY} 次，请点击重试）`;
      } finally {
        this.aiScoreLoading = false;
        this._aiScoreReader = null;
      }
    },
    openAiScore(row) {
      if (!this.canScoreCommit()) {
        this.$Message.warning('仅任务创建人可评分');
        return;
      }
      this.aiScoreRow = row;
      this.aiScoreVisible = true;
      this.runAiScore();
    },
    retryAiScore() {
      this.runAiScore(true);
    },
    closeAiScore() {
      if (this.aiScoreLoading) return;
      this.abortAiScoreRequest();
      this.aiScoreVisible = false;
      this.aiScoreRow = null;
      this.aiScorePhase = 'loading';
      this.aiScoreResult = null;
      this.aiScoreForm = { score: null, suggestion: '' };
      this.aiScoreStreamText = '';
      this.aiScoreRetryCount = 0;
    },
    async submitAiScore() {
      if (!this.aiScoreRow) return false;
      const score = this.aiScoreForm.score;
      if (score === null || score === undefined || score === '') {
        this.$Message.warning('请输入分值');
        return false;
      }
      try {
        await this.patchCommitScore(
          this.aiScoreRow,
          score,
          (this.aiScoreForm.suggestion || '').trim(),
        );
        this.closeAiScore();
        return true;
      } catch (e) {
        return false;
      }
    },
    async fetchCommits() {
      if (this.commitsJobId == null) return;
      this.commitsLoading = true;
      try {
        const res = await this.$api.get(`${API_BASE}/${this.commitsJobId}/commits`);
        this.commitItems = extractList(res);
      } catch (e) {
        this.$Message.error(e.message || '加载提交成果失败');
        this.commitItems = [];
      } finally {
        this.commitsLoading = false;
        this.$nextTick(() => {
          if (this.$refs.commitsTable && this.$refs.commitsTable.calcTableHeight) {
            this.$refs.commitsTable.calcTableHeight();
          }
        });
      }
    },
    async patchCommitScore(row, score, suggestion) {
      if (!this.canScoreCommit() || this.commitsJobId == null) return;
      if (score === null || score === undefined || score === '') return;
      this.scoreSavingId = row.id;
      try {
        const payload = { score: Number(score) };
        if (suggestion !== undefined) {
          payload.suggestion = suggestion;
        }
        await this.$api.patch(`${API_BASE}/${this.commitsJobId}/commits/${row.id}`, payload);
        row.score = Number(score);
        if (suggestion !== undefined) {
          row.suggestion = suggestion;
        }
        row.suggested_at = row.suggested_at || new Date().toISOString();
        this.$Message.success('评分已保存');
      } catch (e) {
        this.$Message.error(e.message || '评分失败');
        await this.fetchCommits();
        throw e;
      } finally {
        this.scoreSavingId = null;
      }
    },
    confirmDeleteCommit(row) {
      if (!this.canDeleteCommit(row)) {
        this.$Message.warning('无权删除该提交');
        return;
      }
      this.$Modal.confirm({
        title: '删除确认',
        content: '确定删除该作业提交？',
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${this.commitsJobId}/commits/${row.id}`);
            this.$Message.success('已删除');
            await this.fetchCommits();
            await this.fetchList();
          } catch (e) {
            this.$Message.error(e.message || '删除失败');
          }
        },
      });
    },
    confirmDeleteJob(row) {
      if (!this.canEditOrDeleteJob(row)) {
        this.$Message.warning('仅管理员或创建人可删除');
        return;
      }
      this.$Modal.confirm({
        title: '删除确认',
        content: `确定删除「${row.name || row.id}」？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('已删除');
            await this.fetchList();
          } catch (e) {
            this.$Message.error(e.message || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.qclaw-jobs-page.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.03) 0%, transparent 32%);
}
.qclaw-jobs-layout {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 14px 32px 20px;
  box-sizing: border-box;
}
.qclaw-jobs-panel {
  max-width: 89%;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg2, #fff);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.qclaw-jobs-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}
.qclaw-jobs-toolbar-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qclaw-jobs-toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}
.qclaw-jobs-toolbar-desc {
  font-size: 12px;
  color: var(--subtext2);
}
.qclaw-jobs-toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
}
.qclaw-jobs-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 12px 12px;
}
.job-name-link,
.commits-count-link,
.job-link,
.commit-link,
.commit-file-link {
  cursor: pointer;
  color: var(--primary);
}
.job-name-link:hover,
.commits-count-link:hover,
.job-link:hover,
.commit-link:hover,
.commit-file-link:hover {
  text-decoration: underline;
}
.job-detail-card {
  padding: 16px 18px;
  margin-bottom: 20px;
  background: var(--bg2, #fafafa);
  border: 1px solid var(--border);
  border-radius: 10px;
}
.job-detail-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.job-detail-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.job-detail-validation {
  font-size: 13px;
}
.job-detail-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.job-detail-meta-time {
  font-size: 12px;
  color: var(--subtext2);
  white-space: nowrap;
}
.job-detail-desc {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed var(--border);
  font-size: 14px;
  line-height: 1.7;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
}
.commits-section {
  position: relative;
  min-height: 80px;
}
.commits-table-wrap {
  min-height: 400px;
  height: 48vh;
  max-height: 560px;
}
.commits-table-wrap >>> .gz-table,
.commits-table-wrap >>> .ivu-table-wrapper {
  height: 100%;
}
.commits-section-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
}
.commits-section-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}
.inner-modal-title {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.35;
}
.commits-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.commits-stat {
  padding: 6px 14px;
  font-size: 13px;
  border-radius: 20px;
  border: 1px solid var(--border);
  background: var(--bg2, #fff);
  color: var(--text);
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}
.commits-stat:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.commits-stat.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.commits-stat--excellent.active {
  background: #19be6b;
  border-color: #19be6b;
}
.commits-stat--qualified.active {
  background: #2d8cf0;
  border-color: #2d8cf0;
}
.commits-stat--unqualified.active {
  background: #ed4014;
  border-color: #ed4014;
}
.commit-action-icons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.commit-table-score {
  font-weight: 600;
  color: var(--primary);
}
.commit-delete-icon {
  color: var(--subtext2) !important;
}
.commit-delete-icon:hover {
  color: #ed4014 !important;
}
.commit-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg2, #fff);
}
.commit-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: var(--bg1, #f7f8fa);
  border-bottom: 1px solid var(--border);
}
.commit-card-user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.commit-card-time {
  font-size: 12px;
  color: var(--subtext2);
  white-space: nowrap;
}
.commit-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.commit-card-score-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.commit-card-score-label {
  font-size: 12px;
  color: var(--subtext2);
}
.commit-card-score-val {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary);
  min-width: 24px;
  text-align: right;
}
.commit-delete-btn {
  color: var(--subtext2) !important;
}
.commit-delete-btn:hover {
  color: #ed4014 !important;
}
.commit-card-body {
  padding: 14px 16px;
  max-height: 320px;
  overflow-y: auto;
}
.commit-card-body--clickable {
  cursor: pointer;
}
.commit-card-body >>> .mdmdt {
  line-height: 1.65;
  color: var(--text);
  font-size: 14px;
}
.commit-expand-link {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
}
.commit-card-files {
  padding: 0 16px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.commit-card-files-label {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--subtext2);
  line-height: 28px;
}
.commit-file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.commit-file-image img {
  max-width: 80px;
  max-height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--border);
}
.commit-card-footer {
  padding: 12px 16px 14px;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.02);
}
.commit-card-footer-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--subtext2);
  margin-bottom: 6px;
}
.commit-card-suggestion {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
}
.commit-card-suggested-at {
  margin-top: 6px;
  font-size: 12px;
  color: var(--subtext2);
}
.commit-card--modal .commit-card-body {
  max-height: 50vh;
}
.commit-detail-body {
  padding: 0;
  background: transparent;
  border: none;
}
</style>

<style lang="less">
.qclaw-job-detail-modal.no-modal-title .ivu-modal-header,
.qclaw-commit-detail-modal.no-modal-title .ivu-modal-header,
.qclaw-ai-score-modal.no-modal-title .ivu-modal-header {
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
}
.qclaw-job-detail-modal.no-modal-title .ivu-modal-header-inner,
.qclaw-commit-detail-modal.no-modal-title .ivu-modal-header-inner,
.qclaw-ai-score-modal.no-modal-title .ivu-modal-header-inner {
  display: none;
}
.modal-header-close {
  position: absolute;
  right: 16px;
  top: 10px;
  z-index: 2;
  color: var(--subtext2);
  cursor: pointer;
  line-height: 1;
}
.modal-header-close:hover {
  color: var(--text);
}
.ai-score-panel {
  min-height: 0;
}
.ai-score-layout {
  display: flex;
  align-items: flex-start;
  gap: 0;
  height: 440px;
  max-height: 55vh;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.ai-score-left {
  flex: 1;
  min-width: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  border-right: 1px solid var(--border);
  background: var(--bg2, #fff);
  overflow: hidden;
}
.ai-score-right {
  width: 340px;
  flex-shrink: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  background: var(--bg2, #fff);
  overflow: hidden;
}
.ai-score-section-title {
  flex-shrink: 0;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  border-bottom: 1px solid var(--border);
  background: var(--bg1, #f7f8fa);
}
.ai-score-md-body {
  flex: 1;
  min-height: 0;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 14px 16px;
  color: var(--text);
  background: var(--bg2, #fff);
}
.ai-score-md-body .mdmdt {
  color: var(--text);
  line-height: 1.65;
}
.ai-score-loading {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 20px 16px 16px;
  overflow-y: auto;
}
.ai-score-form {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ai-score-form-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ai-score-form-row--block {
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}
.ai-score-form-label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}
.ai-score-form-row--block .ai-score-form-label {
  margin-bottom: 4px;
}
.ai-score-form-score {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ai-score-form-unit {
  font-size: 13px;
  color: var(--subtext2);
}
.qclaw-ai-score-modal .ai-score-suggestion-input textarea,
.qclaw-ai-score-modal .ai-score-suggestion-input .ivu-input {
  color: var(--text) !important;
  background: var(--bg2, #fff) !important;
}
.qclaw-ai-score-modal .ivu-input-number-input {
  color: var(--text) !important;
  background: var(--bg2, #fff) !important;
}
.ai-score-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
  background: var(--bg1, #f7f8fa);
}
.ai-score-loading-orbit {
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;
  border-radius: 50%;
  border: 3px solid rgba(45, 140, 240, 0.15);
  border-top-color: var(--primary, #2d8cf0);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: qclaw-ai-spin 1s linear infinite;
  color: var(--primary, #2d8cf0);
}
@keyframes qclaw-ai-spin {
  to { transform: rotate(360deg); }
}
.ai-score-loading-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px;
}
.ai-score-loading-desc {
  font-size: 13px;
  color: var(--subtext2);
  margin: 0;
}
.ai-score-stream-preview {
  margin-top: 16px;
  max-height: 120px;
  overflow: auto;
  text-align: left;
  padding: 10px;
  background: var(--bg1, #f7f8fa);
  border-radius: 6px;
  border: 1px solid var(--border);
}
.ai-score-stream-preview pre {
  margin: 0;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  color: var(--text);
}
.ai-score-error {
  text-align: center;
  padding: 32px 16px;
}
.ai-score-error p {
  margin: 12px 0 16px;
  color: var(--subtext2);
}
</style>
